// ============================================
// PaymentMethodForm Component
// ============================================
// Securely add payment methods using Stripe Elements
// Displays saved payment methods
// Supports card and Pix payments
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AppFooter from '../AppFooter';
import { FaHome, FaReceipt, FaCreditCard, FaPlus } from 'react-icons/fa';
import './PaymentMethodForm.css';

// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ============================================
// Card Payment Form (wrapped in Elements provider)
// ============================================
const PaymentForm = ({ onSuccess, onCancel, tenantId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
      } else {
        // Sync the payment method to our DB immediately (don't wait for webhook)
        if (setupIntent?.payment_method && tenantId) {
          try {
            await axios.post(`/api/billing/payment-method/sync/${tenantId}`, {
              paymentMethodId: setupIntent.payment_method
            });
          } catch (syncErr) {
            console.error('Error syncing payment method:', syncErr);
            // Continue anyway — webhook will eventually sync it
          }
        }
        onSuccess();
      }
    } catch (err) {
      setError('An error occurred while saving your payment method.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <PaymentElement />

      {error && <div className="payment-error">{error}</div>}

      <div className="form-actions">
        <button
          type="button"
          className="btn-cancel"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-submit"
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : 'Save Payment Method'}
        </button>
      </div>
    </form>
  );
};

// ============================================
// Pix Payment Form (wrapped in Elements provider)
// Uses Stripe PaymentElement for native Pix handling
// ============================================
const PixPaymentForm = ({ onSuccess, onCancel, paymentIntentId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, confirming, waiting, succeeded
  const [pixNextAction, setPixNextAction] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);
    setStatus('confirming');

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        setStatus('idle');
        return;
      }

      // Check if payment succeeded immediately (unlikely for Pix)
      if (paymentIntent?.status === 'succeeded') {
        setStatus('succeeded');
        setProcessing(false);
        if (onSuccess) onSuccess();
        return;
      }

      // For Pix: payment requires action (QR code display)
      if (paymentIntent?.status === 'requires_action' && paymentIntent?.next_action) {
        const pixData = paymentIntent.next_action.pix_display_qr_code;
        if (pixData) {
          setPixNextAction(pixData);
        }
        setStatus('waiting');
        setProcessing(false);

        // Poll for payment completion
        pollRef.current = setInterval(async () => {
          try {
            const statusRes = await axios.get(`/api/billing/pix/status/${paymentIntentId}`);
            if (statusRes.data.status === 'succeeded') {
              stopPolling();
              setStatus('succeeded');
              if (onSuccess) onSuccess();
            } else if (statusRes.data.status === 'canceled' || statusRes.data.status === 'requires_payment_method') {
              stopPolling();
              setStatus('idle');
              setError('Payment expired or was canceled. Please try again.');
            }
          } catch (pollErr) {
            console.error('Error polling Pix status:', pollErr);
          }
        }, 5000);
      }
    } catch (err) {
      setError('An error occurred during payment.');
      setProcessing(false);
      setStatus('idle');
    }
  };

  const handleCopyCode = () => {
    if (pixNextAction?.data) {
      navigator.clipboard.writeText(pixNextAction.data);
      alert('Pix code copied to clipboard!');
    }
  };

  if (status === 'succeeded') {
    return (
      <div className="pix-payment-section">
        <div className="pix-success">
          <h3>Payment Received!</h3>
          <p>Your Pix payment was processed successfully.</p>
          <button className="btn-submit" onClick={onCancel}>Done</button>
        </div>
      </div>
    );
  }

  if (status === 'waiting' && pixNextAction) {
    return (
      <div className="pix-payment-section">
        <h3>Complete Your Pix Payment</h3>
        <div className="pix-qr-section">
          {pixNextAction.image_url_png && (
            <div className="pix-qr-code">
              <img src={pixNextAction.image_url_png} alt="Pix QR Code" />
            </div>
          )}

          {pixNextAction.data && (
            <div className="pix-copy-paste">
              <label>Pix Copy & Paste Code</label>
              <div className="pix-code-container">
                <textarea
                  readOnly
                  value={pixNextAction.data}
                  rows={3}
                  className="pix-code-textarea"
                />
                <button
                  type="button"
                  className="btn-action btn-copy"
                  onClick={handleCopyCode}
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          <div className="pix-waiting">
            <div className="pix-spinner" />
            <p>Waiting for payment confirmation...</p>
            <p className="help-text">
              Scan the QR code or paste the code in your banking app.
              This page will update automatically when payment is received.
            </p>
          </div>

          <button type="button" className="btn-cancel" onClick={() => {
            stopPolling();
            setPixNextAction(null);
            setStatus('idle');
            onCancel();
          }}>
            Cancel Payment
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <PaymentElement />

      {error && <div className="payment-error">{error}</div>}

      <div className="form-actions">
        <button
          type="button"
          className="btn-cancel"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-submit btn-pix"
          disabled={!stripe || processing}
        >
          {processing ? 'Processing...' : 'Pay with Pix'}
        </button>
      </div>
    </form>
  );
};

// ============================================
// Pix Payment Wrapper (handles PaymentIntent creation + Elements mounting)
// ============================================
const PixPayment = ({ tenantId, planId, planName, planPrice, onSuccess, onCancel }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [displayAmount, setDisplayAmount] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualAmount, setManualAmount] = useState('');

  // If planPrice is provided, show it; otherwise allow manual entry
  const hasPlanPrice = planPrice && planPrice > 0;

  const handleCreatePayment = async (amountToCharge) => {
    try {
      setLoading(true);
      setError(null);

      const body = {
        description: `On Screen ${planName || 'subscription'} payment`,
        purpose: 'subscription_payment'
      };

      if (planId) {
        body.planId = planId;
      } else {
        body.amount = Math.round(amountToCharge * 100);
      }

      const res = await axios.post(`/api/billing/pix/create-payment/${tenantId}`, body);

      if (res.data.success) {
        setClientSecret(res.data.clientSecret);
        setPaymentIntentId(res.data.paymentIntentId);
        setDisplayAmount((res.data.amount / 100).toFixed(2));
      }
    } catch (err) {
      console.error('Error creating Pix payment:', err);
      setError(err.response?.data?.message || 'Failed to create Pix payment');
    } finally {
      setLoading(false);
    }
  };

  // Auto-create payment intent if plan price is available
  useEffect(() => {
    if (hasPlanPrice && planId) {
      handleCreatePayment(planPrice);
    }
  }, [planId, planPrice]);

  const handleManualSubmit = () => {
    const amt = Number.parseFloat(manualAmount);
    if (!amt || amt <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    handleCreatePayment(amt);
  };

  // If we have a clientSecret, show the Stripe PaymentElement
  if (clientSecret) {
    return (
      <div className="pix-payment-section">
        <h3>Pay with Pix</h3>
        {planName && (
          <div className="pix-plan-banner">
            <strong>{planName} Plan</strong> — R$ {displayAmount}
          </div>
        )}
        {!planName && displayAmount && (
          <div className="pix-plan-banner">
            Payment: R$ {displayAmount}
          </div>
        )}
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PixPaymentForm
            onSuccess={onSuccess}
            onCancel={onCancel}
            paymentIntentId={paymentIntentId}
          />
        </Elements>
      </div>
    );
  }

  // Manual amount entry (when no plan price provided)
  return (
    <div className="pix-payment-section">
      <h3>Pay with Pix</h3>
      <p className="pix-description">
        Pix is an instant payment method for Brazilian bank accounts.
        {hasPlanPrice ? '' : ' Enter the amount in BRL to generate a payment.'}
      </p>

      {error && <div className="payment-error">{error}</div>}

      {loading ? (
        <div className="pix-waiting">
          <div className="pix-spinner" />
          <p>Creating payment...</p>
        </div>
      ) : (
        <div className="pix-amount-form">
          <div className="pix-input-group">
            <label htmlFor="pix-amount">Amount (BRL)</label>
            <div className="pix-input-wrapper">
              <span className="pix-currency">R$</span>
              <input
                id="pix-amount"
                type="number"
                step="0.01"
                min="1"
                placeholder="0.00"
                value={manualAmount}
                onChange={(e) => setManualAmount(e.target.value)}
                className="pix-amount-input"
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-submit btn-pix"
              onClick={handleManualSubmit}
            >
              Continue to Payment
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// Venmo Payment Section
// ============================================
const VenmoPayment = ({ tenantId, onSuccess, onCancel }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('form'); // form | processing | success

  const handleSubmit = async () => {
    const amt = Number.parseFloat(amount);
    if (!amt || amt <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setError(null);
    setStatus('processing');

    try {
      const res = await axios.post(`/api/venmo/create-order/${tenantId}`, {
        amount: amt,
        note: note || 'Payment via Venmo',
        description: note || 'Payment via Venmo'
      });

      if (res.data.success) {
        // Order created — now we need to capture it
        // For direct Venmo links, redirect the user
        const captureRes = await axios.post(`/api/venmo/capture-order/${tenantId}`, {
          orderId: res.data.orderId
        });

        if (captureRes.data.success && captureRes.data.status === 'COMPLETED') {
          setStatus('success');
          if (onSuccess) onSuccess();
        } else {
          setError('Payment was not completed. Please try again.');
          setStatus('form');
        }
      }
    } catch (err) {
      console.error('Venmo payment error:', err);
      setError(err.response?.data?.message || 'Failed to process Venmo payment');
      setStatus('form');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="venmo-payment-section">
        <div className="pix-success">
          <h3>Payment Complete!</h3>
          <p>Your Venmo payment was processed successfully.</p>
          <button className="btn-submit btn-venmo" onClick={onCancel}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="venmo-payment-section">
      <h3>Pay with Venmo</h3>
      <p className="pix-description">
        Pay directly using Venmo via PayPal. Enter the amount in USD.
      </p>

      {error && <div className="payment-error">{error}</div>}

      {status === 'processing' ? (
        <div className="pix-waiting">
          <div className="pix-spinner" style={{ borderTopColor: '#008CFF' }} />
          <p>Processing Venmo payment...</p>
        </div>
      ) : (
        <div className="pix-amount-form">
          <div className="pix-input-group">
            <label htmlFor="venmo-amount">Amount (USD)</label>
            <div className="pix-input-wrapper" style={{ borderColor: '#008CFF' }}>
              <span className="pix-currency">$</span>
              <input
                id="venmo-amount"
                type="number"
                step="0.01"
                min="1"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pix-amount-input"
              />
            </div>
          </div>
          <div className="pix-input-group">
            <label htmlFor="venmo-note">Note (optional)</label>
            <input
              id="venmo-note"
              type="text"
              placeholder="What's this payment for?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-submit btn-venmo"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Pay with Venmo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// Main Component
// ============================================
const PaymentMethodForm = ({ tenantId }) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const actualTenantId = tenantId || params.tenant;
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPixForm, setShowPixForm] = useState(false);
  const [showVenmoForm, setShowVenmoForm] = useState(false);
  const [venmoEnabled, setVenmoEnabled] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Plan info passed from SubscriptionPlans via navigation state
  const planState = location.state || {};
  const { planId, planName, planPrice, message: planMessage } = planState;

  useEffect(() => {
    fetchPaymentMethods();
    fetchVenmoStatus();
  }, [actualTenantId]);

  // Check if tenant has Venmo enabled
  const fetchVenmoStatus = async () => {
    try {
      const res = await axios.get(`/api/venmo/settings/${actualTenantId}`);
      if (res.data?.success) {
        setVenmoEnabled(
          !!res.data.settings.venmo_stripe_enabled || !!res.data.settings.venmo_direct_enabled
        );
      }
    } catch {
      // Venmo not available — that's fine
    }
  };

  // Auto-open Pix form when navigated from subscription with plan info
  useEffect(() => {
    if (planId && planPrice) {
      setShowPixForm(true);
      setShowAddForm(false);
      setShowVenmoForm(false);
    }
  }, [planId, planPrice]);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/billing/payment-methods/${actualTenantId}`);
      setPaymentMethods(res.data.paymentMethods || []);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      setError(null);
      const res = await axios.post(`/api/billing/payment-method/setup-intent/${actualTenantId}`);
      setClientSecret(res.data.clientSecret);
      setShowAddForm(true);
      setShowPixForm(false);
      setShowVenmoForm(false);
    } catch (err) {
      console.error('Error creating setup intent:', err);
      setError(err.response?.data?.message || 'Failed to initialize payment form');
    }
  };

  const handleShowPixForm = () => {
    setShowPixForm(true);
    setShowAddForm(false);
    setShowVenmoForm(false);
    setClientSecret('');
    setError(null);
  };

  const handleShowVenmoForm = () => {
    setShowVenmoForm(true);
    setShowAddForm(false);
    setShowPixForm(false);
    setClientSecret('');
    setError(null);
  };

  const handlePaymentSuccess = async () => {
    setShowAddForm(false);
    setClientSecret('');
    await fetchPaymentMethods();
    alert('Payment method added successfully!');
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setShowPixForm(false);
    setShowVenmoForm(false);
    setClientSecret('');
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await axios.post(`/api/billing/payment-methods/${actualTenantId}/${paymentMethodId}/default`);
      await fetchPaymentMethods();
      alert('Default payment method updated');
    } catch (err) {
      console.error('Error setting default payment method:', err);
      alert('Failed to update default payment method');
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      await axios.delete(`/api/billing/payment-methods/${actualTenantId}/${paymentMethodId}`);
      await fetchPaymentMethods();
      alert('Payment method removed');
    } catch (err) {
      console.error('Error removing payment method:', err);
      alert('Failed to remove payment method');
    }
  };

  const getCardBrandIcon = (brand) => {
    const brandLower = brand?.toLowerCase();
    switch (brandLower) {
      case 'visa':
        return '💳 Visa';
      case 'mastercard':
        return '💳 Mastercard';
      case 'amex':
        return '💳 Amex';
      case 'discover':
        return '💳 Discover';
      default:
        return '💳 Card';
    }
  };

  if (loading) {
    return <div className="payment-loading">Loading payment methods...</div>;
  }

  const showingForm = showAddForm || showPixForm || showVenmoForm;

  return (
    <div className="payment-methods" style={{ position: 'relative', paddingBottom: '72px' }}>
      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(`/${actualTenantId}/dashboard`)}>
          <FaHome /> Home
        </button>
        <button type="button" onClick={() => navigate(`/${actualTenantId}/billing`)}>
          <FaReceipt /> Billing
        </button>
        <button type="button" className="btn-primary-action" onClick={handleAddPaymentMethod}>
          <FaPlus /> Add Card
        </button>
      </div>

      <div className="payment-header">
        <h2>Payment Methods</h2>
        {!showingForm && (
          <div className="payment-header-actions">
            <button className="btn-add" onClick={handleAddPaymentMethod}>
              Add Card
            </button>
            <button className="btn-add btn-pix" onClick={handleShowPixForm}>
              Pay with Pix
            </button>
            {venmoEnabled && (
              <button className="btn-add btn-venmo" onClick={handleShowVenmoForm}>
                Pay with Venmo
              </button>
            )}
          </div>
        )}
      </div>

      {/* Plan message banner from subscription navigation */}
      {planMessage && !showingForm && (
        <div className="pix-plan-banner">{planMessage}</div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Card Payment Form */}
      {showAddForm && clientSecret && (
        <div className="add-payment-form">
          <div className="form-top-bar">
            <h3>Add New Payment Method</h3>
            <button
              type="button"
              className="btn-close-form"
              onClick={handleCancelAdd}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              onSuccess={handlePaymentSuccess}
              onCancel={handleCancelAdd}
              tenantId={actualTenantId}
            />
          </Elements>
        </div>
      )}

      {/* Pix Payment Form */}
      {showPixForm && (
        <div className="add-payment-form">
          <div className="form-top-bar">
            <span />
            <button
              type="button"
              className="btn-close-form"
              onClick={handleCancelAdd}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <PixPayment
            tenantId={actualTenantId}
            planId={planId}
            planName={planName}
            planPrice={planPrice}
            onSuccess={() => {
              setShowPixForm(false);
              fetchPaymentMethods();
            }}
            onCancel={handleCancelAdd}
          />
        </div>
      )}

      {/* Venmo Payment Form */}
      {showVenmoForm && (
        <div className="add-payment-form">
          <div className="form-top-bar">
            <span />
            <button
              type="button"
              className="btn-close-form"
              onClick={handleCancelAdd}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <VenmoPayment
            tenantId={actualTenantId}
            onSuccess={() => {
              setShowVenmoForm(false);
              fetchPaymentMethods();
            }}
            onCancel={handleCancelAdd}
          />
        </div>
      )}

      {/* No methods message */}
      {!showingForm && paymentMethods.length === 0 && (
        <div className="no-methods">
          <p>No payment methods saved</p>
          <p className="help-text">Add a payment method to manage your subscription</p>
        </div>
      )}

      {/* Saved payment methods list */}
      {!showingForm && paymentMethods.length > 0 && (
        <div className="methods-list">
          {paymentMethods.map(method => (
            <div key={method.id} className="method-card">
              <div className="method-info">
                <div className="method-brand">
                  {getCardBrandIcon(method.card_brand)}
                </div>
                <div className="method-details">
                  <div className="method-number">•••• {method.card_last4}</div>
                  <div className="method-expiry">
                    Expires {method.card_exp_month}/{method.card_exp_year}
                  </div>
                </div>
                {method.is_default === 1 && (
                  <div className="default-badge">Default</div>
                )}
              </div>
              <div className="method-actions">
                {method.is_default === 0 && (
                  <button
                    className="btn-action"
                    onClick={() => handleSetDefault(method.stripe_payment_method_id)}
                  >
                    Set as Default
                  </button>
                )}
                <button
                  className="btn-remove"
                  onClick={() => handleRemovePaymentMethod(method.stripe_payment_method_id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* App Footer */}
      <AppFooter
        buttons={[
          {
            icon: <FaReceipt />,
            label: "Billing",
            onClick: () => navigate(`/${actualTenantId}/billing`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${actualTenantId}/dashboard`),
          },
          {
            icon: <FaCreditCard />,
            label: "Payment",
            onClick: () => navigate(`/${actualTenantId}/billing/payment-methods`),
          },
        ]}
      />
    </div>
  );
};

export default PaymentMethodForm;